import { useCallback, useEffect, useRef, useState } from 'react';

import { columnsStyle, containerStyle, rowsStyle } from 'styles/global.styles';

import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  MenuItem,
  Pagination,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';

import ContentPage from 'components/ui/content';
import CustomPagination from 'components/ui/pagination/Pagination';
import SkeletonTableLoading from 'components/ui/skeletonTable/SkeletonTable';

import {
  AccessHistoryItem,
  FranchiseAccessService,
  FranchiseAccessUser,
} from 'services/FranchiseAccess/FranchiseAccessService';
import { FranchiseService } from 'services/FranchiseService/FranchiseService';

import { IFranchise } from 'domains/franchise';

export const accessLabels = { Active: 'Ativo', Pending: 'Pendente', Revoked: 'Revogado' };

export default function FranchiseAccessPage() {
  const [users, setUsers] = useState<FranchiseAccessUser[]>([]);
  const [franchises, setFranchises] = useState<IFranchise[]>([]);
  const [search, setSearch] = useState('');
  const [filter, setFilter] = useState('');
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [error, setError] = useState('');
  const [listFailed, setListFailed] = useState(false);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [selected, setSelected] = useState<FranchiseAccessUser | null>(null);
  const [revoke, setRevoke] = useState(false);
  const [reason, setReason] = useState('');
  const [franchise, setFranchise] = useState('');
  const [historyUser, setHistoryUser] = useState<FranchiseAccessUser | null>(null);
  const [history, setHistory] = useState<AccessHistoryItem[]>([]);
  const [historyPage, setHistoryPage] = useState(1);
  const [historyTotal, setHistoryTotal] = useState(0);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [dialogError, setDialogError] = useState('');
  const listRequest = useRef(0);

  const load = useCallback(async () => {
    listRequest.current += 1;
    const request = listRequest.current;
    setLoading(true);
    try {
      const data = await FranchiseAccessService.list(page, search, filter);
      if (request === listRequest.current) {
        setUsers(data.users);
        setTotal(data.total);
        setListFailed(false);
        setError('');
      }
    } catch (err) {
      if (request === listRequest.current) {
        setUsers([]);
        setListFailed(true);
        setError(
          err?.response?.status === 403
            ? 'Você não tem permissão para consultar os acessos. Entre novamente ou contate o administrador do sistema.'
            : 'Não foi possível consultar os acessos. Tente novamente.',
        );
      }
    } finally {
      if (request === listRequest.current) setLoading(false);
    }
  }, [page, search, filter]);
  useEffect(() => {
    load();
  }, [load]);
  useEffect(() => {
    FranchiseService.getAll()
      .then(data => setFranchises(data.data.franchises))
      .catch(() => setError('Não foi possível carregar as franquias.'));
  }, []);
  useEffect(() => {
    if (!historyUser) return undefined;
    let active = true;
    setHistoryLoading(true);
    setHistory([]);
    setDialogError('');
    FranchiseAccessService.history(historyUser.userId, historyPage)
      .then(data => {
        if (active) {
          setHistory(data.items);
          setHistoryTotal(data.total);
        }
      })
      .catch(() => {
        if (active) setDialogError('Não foi possível consultar o histórico.');
      })
      .finally(() => {
        if (active) setHistoryLoading(false);
      });
    return () => {
      active = false;
    };
  }, [historyUser, historyPage]);
  const open = (user: FranchiseAccessUser, isRevoke: boolean) => {
    setSelected(user);
    setRevoke(isRevoke);
    setReason('');
    setFranchise(user.franchiseId || '');
    setDialogError('');
  };
  const save = async () => {
    if (!selected) return;
    setSaving(true);
    setDialogError('');
    try {
      await FranchiseAccessService.change(selected, revoke, reason.trim(), franchise);
      setSelected(null);
      setError('');
      await load();
    } catch (err) {
      if (err?.response?.status === 409) {
        setSelected(null);
        await load();
        setError('Este acesso foi alterado por outra pessoa. Confira os dados e tente novamente.');
      } else
        setDialogError(
          err?.response?.data?.error ||
            'Não foi possível alterar o acesso. Verifique sua permissão e tente novamente.',
        );
    } finally {
      setSaving(false);
    }
  };
  const franchiseName = (id?: string) =>
    franchises.find(item => item.id === id)?.name || id || 'Nenhuma';

  return (
    <Box sx={{ ...containerStyle, flexDirection: 'column' }}>
      <ContentPage title="Acessos de franquia">
        <Typography sx={{ mt: 3, mb: 3, fontSize: 14 }}>
          O usuário deve entrar no sistema uma vez para aparecer nesta lista. A concessão permite
          administrar somente a franquia selecionada.
        </Typography>
        {error && (
          <Alert severity="error" onClose={() => setError('')} sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} sx={{ mb: 2 }}>
          <TextField
            size="small"
            label="Buscar por nome ou e-mail"
            value={search}
            onChange={e => {
              setSearch(e.target.value);
              setPage(1);
            }}
          />
          <TextField
            size="small"
            select
            label="Situação"
            value={filter}
            sx={{ minWidth: 160 }}
            onChange={e => {
              setFilter(e.target.value);
              setPage(1);
            }}
          >
            <MenuItem value="">Todas</MenuItem>
            {Object.entries(accessLabels).map(([key, label]) => (
              <MenuItem key={key} value={key}>
                {label}
              </MenuItem>
            ))}
          </TextField>
          <Button
            onClick={() => {
              setError('');
              load();
            }}
            disabled={loading}
          >
            Atualizar
          </Button>
        </Stack>
        <TableContainer
          component={Paper}
          sx={{ boxShadow: 'rgba(50, 50, 93, 0.25) 0px 0px 5px 0px' }}
        >
          <Table sx={{ minWidth: 650 }} aria-label="Acessos de franquia">
            <TableHead>
              <TableRow>
                {['Usuário', 'Situação', 'Franquia', 'Ações'].map(label => (
                  <TableCell key={label} sx={columnsStyle}>
                    {label}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {loading && (
                <TableRow>
                  <TableCell colSpan={4}>
                    <Box role="status" aria-label="Carregando acessos">
                      <SkeletonTableLoading />
                    </Box>
                  </TableCell>
                </TableRow>
              )}
              {!loading && !listFailed && users.length === 0 && (
                <TableRow>
                  <TableCell colSpan={4}>
                    <Typography variant="caption">Nenhum registro encontrado...</Typography>
                  </TableCell>
                </TableRow>
              )}
              {!loading &&
                !listFailed &&
                users.map(user => (
                  <TableRow
                    key={user.userId}
                    sx={{ '&:last-child td, &:last-child th': { border: 0 } }}
                  >
                    <TableCell align="left" component="th" scope="row">
                      <Typography sx={rowsStyle}>{user.name}</Typography>
                      <Typography sx={rowsStyle}>{user.email}</Typography>
                      <Typography fontSize={12} color="text.secondary">
                        ID: {user.subject}
                      </Typography>
                    </TableCell>
                    <TableCell align="left">
                      <Typography sx={rowsStyle}>{accessLabels[user.status]}</Typography>
                    </TableCell>
                    <TableCell align="left">
                      <Typography sx={rowsStyle}>{user.franchiseName || 'Nenhuma'}</Typography>
                    </TableCell>
                    <TableCell align="left">
                      <Button size="small" onClick={() => open(user, false)}>
                        {user.status === 'Active' ? 'Alterar franquia' : 'Conceder acesso'}
                      </Button>
                      {user.status === 'Active' && (
                        <Button size="small" color="error" onClick={() => open(user, true)}>
                          Revogar
                        </Button>
                      )}
                      <Button
                        size="small"
                        onClick={() => {
                          setHistoryUser(user);
                          setHistoryPage(1);
                        }}
                      >
                        Histórico
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
            </TableBody>
          </Table>
        </TableContainer>
        {!listFailed && (
          <CustomPagination
            page={page}
            total={Math.max(1, Math.ceil(total / 20))}
            setPage={setPage}
            isLoading={loading}
          />
        )}
        <Dialog
          open={!!selected}
          onClose={() => {
            if (!saving) setSelected(null);
          }}
          fullWidth
          maxWidth="sm"
        >
          <DialogTitle>{revoke ? 'Revogar acesso' : 'Conceder acesso à franquia'}</DialogTitle>
          <DialogContent>
            <Typography sx={{ mb: 2 }}>
              {selected?.name} — {selected?.email}
            </Typography>
            {dialogError && (
              <Alert severity="error" sx={{ mb: 2 }}>
                {dialogError}
              </Alert>
            )}
            {revoke ? (
              <Alert severity="warning" sx={{ mb: 2 }}>
                O usuário perderá o acesso nas próximas requisições, inclusive com a sessão aberta.
              </Alert>
            ) : (
              <TextField
                select
                fullWidth
                label="Franquia"
                value={franchise}
                onChange={e => setFranchise(e.target.value)}
                sx={{ my: 1 }}
              >
                {franchises.map(item => (
                  <MenuItem key={item.id} value={item.id}>
                    {item.name}
                  </MenuItem>
                ))}
              </TextField>
            )}
            <TextField
              fullWidth
              multiline
              minRows={3}
              label="Justificativa"
              value={reason}
              onChange={e => setReason(e.target.value)}
              inputProps={{ maxLength: 500 }}
              helperText="De 3 a 500 caracteres. Esta alteração ficará registrada no histórico."
              sx={{ mt: 2 }}
            />
          </DialogContent>
          <DialogActions>
            <Button disabled={saving} onClick={() => setSelected(null)}>
              Cancelar
            </Button>
            <Button
              variant="contained"
              color={revoke ? 'error' : 'primary'}
              disabled={saving || reason.trim().length < 3 || (!revoke && !franchise)}
              onClick={save}
            >
              {saving ? 'Salvando...' : 'Confirmar'}
            </Button>
          </DialogActions>
        </Dialog>
        <Dialog open={!!historyUser} onClose={() => setHistoryUser(null)} fullWidth maxWidth="md">
          <DialogTitle>Histórico — {historyUser?.name}</DialogTitle>
          <DialogContent>
            {dialogError && <Alert severity="error">{dialogError}</Alert>}
            {historyLoading ? (
              <Typography>Carregando histórico...</Typography>
            ) : (
              history.map(item => (
                <Box
                  key={`${item.createdAt}-${item.actor}-${item.status}`}
                  sx={{ py: 2, borderBottom: '1px solid #ddd' }}
                >
                  <Typography>
                    {new Date(item.createdAt).toLocaleString('pt-BR')} —{' '}
                    {accessLabels[item.previousStatus]} → {accessLabels[item.status]}
                  </Typography>
                  <Typography>
                    Franquia: {franchiseName(item.previousFranchiseId)} →{' '}
                    {franchiseName(item.franchiseId)}
                  </Typography>
                  <Typography>{item.reason}</Typography>
                  <Typography variant="caption">Administrador: {item.actor}</Typography>
                </Box>
              ))
            )}
            {!historyLoading && !dialogError && !history.length && (
              <Typography>Nenhuma alteração registrada.</Typography>
            )}
            <Pagination
              page={historyPage}
              count={Math.max(1, Math.ceil(historyTotal / 20))}
              onChange={(_, value) => setHistoryPage(value)}
              sx={{ mt: 2 }}
            />
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setHistoryUser(null)}>Fechar</Button>
          </DialogActions>
        </Dialog>
      </ContentPage>
    </Box>
  );
}
